import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react'
import * as React from 'react'

import {
	Button,
	type ButtonProps,
	buttonVariants,
} from '#app/components/ui/button'
import { cn } from '#app/lib/utils'

const Pagination = ({ className, ...props }: React.ComponentProps<'nav'>) => (
	<nav
		role="navigation"
		aria-label="pagination"
		className={cn('mx-auto flex w-full justify-center', className)}
		{...props}
	/>
)
Pagination.displayName = 'Pagination'

const PaginationContent = React.forwardRef<
	HTMLUListElement,
	React.ComponentProps<'ul'>
>(({ className, ...props }, ref) => (
	<ul
		ref={ref}
		className={cn('flex flex-row items-center gap-1', className)}
		{...props}
	/>
))
PaginationContent.displayName = 'PaginationContent'

const PaginationItem = React.forwardRef<
	HTMLLIElement,
	React.ComponentProps<'li'>
>(({ className, ...props }, ref) => (
	<li ref={ref} className={cn('', className)} {...props} />
))
PaginationItem.displayName = 'PaginationItem'

type PaginationLinkProps = {
	isActive?: boolean
} & Pick<ButtonProps, 'size'> &
	React.ComponentProps<'a'>

const PaginationLink = ({
	className,
	isActive,
	size = 'icon',
	...props
}: PaginationLinkProps) => (
	<a
		aria-current={isActive ? 'page' : undefined}
		className={cn(
			buttonVariants({
				variant: isActive ? 'outline' : 'ghost',
				size,
			}),
			className,
		)}
		{...props}
	/>
)
PaginationLink.displayName = 'PaginationLink'

const PaginationPrevious = ({
	className,
	...props
}: React.ComponentProps<typeof PaginationLink>) => (
	<PaginationLink
		aria-label="Go to previous page"
		size="default"
		className={cn('gap-1 pl-2.5', className)}
		{...props}
	>
		<ChevronLeft className="h-4 w-4" />
		<span>Previous</span>
	</PaginationLink>
)
PaginationPrevious.displayName = 'PaginationPrevious'

const PaginationNext = ({
	className,
	...props
}: React.ComponentProps<typeof PaginationLink>) => (
	<PaginationLink
		aria-label="Go to next page"
		size="default"
		className={cn('gap-1 pr-2.5', className)}
		{...props}
	>
		<span>Next</span>
		<ChevronRight className="h-4 w-4" />
	</PaginationLink>
)
PaginationNext.displayName = 'PaginationNext'

const PaginationEllipsis = ({
	className,
	...props
}: React.ComponentProps<'span'>) => (
	<span
		aria-hidden
		className={cn('flex h-9 w-9 items-center justify-center', className)}
		{...props}
	>
		<MoreHorizontal className="h-4 w-4" />
		<span className="sr-only">More pages</span>
	</span>
)
PaginationEllipsis.displayName = 'PaginationEllipsis'

interface SimplePaginationProps {
	currentPage: number
	totalPages: number
	onPageChange: (page: number) => void
	className?: string
}

const SimplePagination: React.FC<SimplePaginationProps> = ({
	currentPage,
	totalPages,
	onPageChange,
	className,
}) => {
	if (totalPages <= 1) return null

	const getPageNumbers = () => {
		const pageNumbers = []
		const maxPagesToShow = 5

		if (totalPages <= maxPagesToShow) {
			for (let i = 1; i <= totalPages; i++) {
				pageNumbers.push(i)
			}
		} else {
			pageNumbers.push(1)

			let start = Math.max(2, currentPage - 1)
			let end = Math.min(totalPages - 1, currentPage + 1)

			if (currentPage <= 2) {
				end = Math.min(totalPages - 1, 4)
			} else if (currentPage >= totalPages - 1) {
				start = Math.max(2, totalPages - 3)
			}

			if (start > 2) {
				pageNumbers.push('ellipsis-start')
			}

			for (let i = start; i <= end; i++) {
				pageNumbers.push(i)
			}

			if (end < totalPages - 1) {
				pageNumbers.push('ellipsis-end')
			}

			if (totalPages > 1) {
				pageNumbers.push(totalPages)
			}
		}

		return pageNumbers
	}

	return (
		<Pagination className={className}>
			<PaginationContent>
				<PaginationItem>
					<Button
						variant="outline"
						size="icon"
						onClick={() => onPageChange(currentPage - 1)}
						disabled={currentPage === 1}
					>
						<ChevronLeft className="h-4 w-4" />
					</Button>
				</PaginationItem>

				{getPageNumbers().map((page, index) =>
					typeof page === 'number' ? (
						<PaginationItem key={index}>
							<Button
								variant={currentPage === page ? 'default' : 'outline'}
								size="sm"
								onClick={() => onPageChange(page)}
								className="min-w-8"
							>
								{page}
							</Button>
						</PaginationItem>
					) : (
						<PaginationItem key={index}>
							<PaginationEllipsis />
						</PaginationItem>
					),
				)}

				<PaginationItem>
					<Button
						variant="outline"
						size="icon"
						onClick={() => onPageChange(currentPage + 1)}
						disabled={currentPage === totalPages}
					>
						<ChevronRight className="h-4 w-4" />
					</Button>
				</PaginationItem>
			</PaginationContent>
		</Pagination>
	)
}

export {
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
	SimplePagination,
}
